import { useState } from 'react';
import { cn, useTheme } from '@v2/design-system';
import { flagUrl, resolveV2Locale, V2_LOCALES, type V2LocaleCode } from '@v2/config/locales';
import { useI18n } from '@v2/providers/I18nProvider';

function FlagImg({ flagCode, size = 20 }: { flagCode: string; size?: number }) {
    return (
        <img
            src={flagUrl(flagCode)}
            width={size}
            height={size}
            alt=""
            className="shrink-0 rounded-full object-cover ring-1 ring-black/10"
        />
    );
}

export default function HeaderLanguageSwitcher() {
    const { theme } = useTheme();
    const { locale, setLocale, isChangingLocale } = useI18n();
    const [pending, setPending] = useState(false);

    const isDark = theme === 'dark';
    const currentCode = locale as V2LocaleCode;
    const current = resolveV2Locale(currentCode);

    const boxClass = isDark
        ? 'border-gray-600 bg-gray-800'
        : 'border-gray-200 bg-white shadow-sm';

    const onChange = async (code: string) => {
        if (code === currentCode || pending || isChangingLocale) {
            return;
        }

        setPending(true);
        try {
            await setLocale(code as V2LocaleCode);
        } finally {
            setPending(false);
        }
    };

    return (
        <label
            className={cn(
                'flex cursor-pointer items-center gap-2 rounded-lg border py-1 pl-2 pr-1 text-xs sm:py-1.5 sm:pl-2.5',
                boxClass,
                (pending || isChangingLocale) && 'opacity-70',
            )}
            title="Idioma da interface"
        >
            <FlagImg flagCode={current.flagCode} size={22} />
            <select
                value={currentCode}
                disabled={pending || isChangingLocale}
                onChange={(e) => void onChange(e.target.value)}
                aria-label="Selecionar idioma"
                className={cn(
                    'max-w-[7.5rem] cursor-pointer border-0 bg-transparent py-0.5 text-xs font-medium outline-none focus:ring-0 sm:max-w-[8.5rem] sm:text-sm',
                    isDark ? 'text-gray-200' : 'text-gray-800',
                )}
            >
                {V2_LOCALES.map((item) => (
                    <option key={item.code} value={item.code}>
                        {item.label}
                    </option>
                ))}
            </select>
        </label>
    );
}
